/**
 * Блоки паспорта контура в разметке K2 — общие для фикстурной ветки
 * (`ContourDetailFixtures`) и канона (`ContourCanon`). Разметка одна, данные
 * собирает каждая ветка (Решение 10 плана `astra-on-canon`).
 */

import type { ReactNode } from 'react';
import { Card, Grid, Stack } from '../ui/Card';
import { KpiCard } from '../ui/KpiCard';
import { DataTable, type Column } from '../ui/DataTable';
import { Badge } from '../ui/Badge';
import { MapWidget } from '../map/MapWidget';
import { dateShort, windowLabel, windowShort } from '../lib/format';
import type { Crop } from '../data/types';
import { extraOutsideNote } from './my/extraWorks';
import { defineMessages } from '../../i18n/define';
import { useT, UNIT_LABELS } from '../../i18n/hooks';
import { tr } from '../../i18n/runtime';

const M = defineMessages({
  purpose: { id: 'contour.passport.purpose', defaultMessage: 'Паспорт контура: землепользование, размещение и происхождение геометрии. Районных и массивных долей здесь нет — у них другой знаменатель.', description: 'Описание под заголовком паспорта контура (старая карточка контура)' },
  kpiArea: { id: 'contour.passport.kpi.area', defaultMessage: 'Площадь', description: 'Подпись KPI паспорта: площадь контура, га земли' },
  noData: { id: 'contour.passport.kpi.noData', defaultMessage: 'нет данных', description: 'Значение KPI «Площадь», когда источник площадь не назвал' },
  landUseTitle: { id: 'contour.passport.landUse.title', defaultMessage: 'Землепользование', description: 'Заголовок карточки землепользования в паспорте контура' },
  landUseFarm: { id: 'contour.passport.landUse.farm', defaultMessage: 'Хозяйство:', description: 'Подпись перед названием хозяйства в карточке землепользования (само название — ссылка или текст — идёт следом)' },
  colWork: { id: 'contour.passport.works.col.work', defaultMessage: 'Работа', description: 'Колонка таблицы работ контура в паспорте: название работы' },
  colCycle: { id: 'contour.passport.works.col.cycle', defaultMessage: 'Агроцикл', description: 'Колонка таблицы работ контура в паспорте: агроцикл (культура × сезон); узкая колонка' },
  colWindow: { id: 'contour.passport.works.col.window', defaultMessage: 'Окно', description: 'Колонка таблицы работ контура в паспорте: окно дат; узкая колонка' },
  noWindow: { id: 'contour.passport.works.noWindow', defaultMessage: 'без срока', description: 'Ячейка колонки «Окно», когда у работы нет окна дат' },
  colState: { id: 'contour.passport.works.col.state', defaultMessage: 'Состояние', description: 'Колонка таблицы работ контура в паспорте: состояние работы' },
  worksUnit: { id: 'contour.passport.works.denominator', defaultMessage: '{count, plural, one {работа} few {работы} many {работ} other {работы}}', description: 'Слово-знаменатель под таблицей работ контура: «5 из 12 работ»; без числа' },
  worksEmpty: { id: 'contour.passport.works.empty', defaultMessage: 'Работ на контуре нет', description: 'Заголовок пустой таблицы работ контура в паспорте' },
  worksTitle: { id: 'contour.passport.works.title', defaultMessage: 'Работы на контуре', description: 'Заголовок карточки работ на контуре в паспорте' },
  voluntaryTitle: { id: 'contour.passport.works.voluntary', defaultMessage: 'Добровольные работы', description: 'Подзаголовок таблицы добровольных работ (сверх плана) под работами плана' },
  factsEmpty: { id: 'contour.passport.quarantine.empty', defaultMessage: 'Обследований по этому контуру в срезе нет.', description: 'Пустое состояние списка обследований контура в паспорте' },
  mapTitle: { id: 'contour.passport.map.title', defaultMessage: 'Контур на карте', description: 'Заголовок карточки с картой контура в паспорте' },
  mapCaption: { id: 'contour.passport.map.caption', defaultMessage: 'Принятая геометрия — сплошная обводка, кандидат — медный штрих', description: 'Подпись под картой контура: как читать обводку (принятая геометрия и кандидат)' },
});


export const contourPurpose = (): string => tr(M.purpose);

/* ---------------------------------------------------------------- KPI */

export interface KpiText {
  label: string;
  value: ReactNode;
  note: string;
}

/**
 * KPI 34/40 — кегль для чисел. Категория и культура пишутся словами, и на
 * 34 px они перебивают единственное число экрана — они идут в `kpi-text`.
 */
export function ContourKpis({
  area,
  category,
  crop,
  winter,
}: {
  /** Площадь, га, строкой; null — источник её не назвал. */
  area: { value: string | null; note: string };
  category: KpiText;
  crop: KpiText;
  winter: KpiText;
}) {
  const t = useT();
  return (
    <div data-guide="kpi">
    <Grid cols={4}>
      {area.value === null ? (
        <div className="kpi-text">
          <KpiCard label={t(M.kpiArea)} value={t(M.noData)} note={area.note} />
        </div>
      ) : (
        <KpiCard label={t(M.kpiArea)} value={area.value} unit={t(UNIT_LABELS.ha)} note={area.note} />
      )}
      {[category, crop, winter].map((kpi) => (
        <div className="kpi-text" key={kpi.label}>
          <KpiCard label={kpi.label} value={kpi.value} note={kpi.note} />
        </div>
      ))}
    </Grid>
    </div>
  );
}

/* ------------------------------------------------------- землепользование */

export function LandUseCard({
  farm,
  caption,
  children,
}: {
  farm: ReactNode;
  caption: string;
  children?: ReactNode;
}) {
  const t = useT();
  return (
    <Card title={t(M.landUseTitle)}>
      <div data-guide="land-use">
      <Stack tight>
        <p className="t-body">{t(M.landUseFarm)} {farm}</p>
        {children}
        <p className="t-caption">{caption}</p>
      </Stack>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------- работы */

export interface ContourWorkLine {
  key: string;
  work: ReactNode;
  cycle: ReactNode;
  windowFrom: string | null;
  windowTo: string | null;
  state: { text: string; quiet: boolean; alert?: boolean };
}

const workColumns = (t: ReturnType<typeof useT>): Column<ContourWorkLine>[] => [
  { key: 'work', header: t(M.colWork), render: (row) => row.work },
  { key: 'cycle', header: t(M.colCycle), render: (row) => row.cycle },
  {
    key: 'window',
    header: t(M.colWindow),
    width: '150px',
    render: (row) =>
      row.windowFrom || row.windowTo ? (
        <span className="num cell-win">
          <span className="cell-win__long">{windowLabel(row.windowFrom, row.windowTo)}</span>
          <span className="cell-win__short">{windowShort(row.windowFrom, row.windowTo)}</span>
        </span>
      ) : (
        t(M.noWindow)
      ),
  },
  {
    key: 'state',
    header: t(M.colState),
    render: (row) => (
      <Badge tone={row.state.alert ? 'copper' : row.state.quiet ? 'outline' : 'sage'}>
        {row.state.text}
      </Badge>
    ),
  },
];

export function ContourWorksTable({
  rows,
  emptyReason,
}: {
  rows: ContourWorkLine[];
  emptyReason: string;
}) {
  const t = useT();
  return (
    <DataTable
      columns={workColumns(t)}
      rows={rows}
      getKey={(row) => row.key}
      denominator={M.worksUnit}
      empty={{ title: t(M.worksEmpty), reason: emptyReason }}
    />
  );
}

/**
 * Карточка «Работы на контуре»: шаблонные работы и отдельной таблицей —
 * добровольные. Отдельная, а не общий список: счётчик «работ» над первой —
 * знаменатель шаблонных обязательств контура, и внеплановая запись в него не
 * входит (WATER-WORKS-SPEC).
 */
export function ContourWorksCard({ works, extra }: { works: ReactNode; extra: ReactNode }) {
  const t = useT();
  return (
    <Card size="lg" title={t(M.worksTitle)}>
      <div data-guide="works">
      <Stack tight>
        {works}
        <div className="t-label muted">{t(M.voluntaryTitle)}</div>
        <p className="t-caption">{extraOutsideNote()}</p>
        {extra}
      </Stack>
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------- карантин */

export interface QuarantineFactLine {
  key: string;
  date: string;
  text: ReactNode;
}

export function QuarantineFacts({ lines }: { lines: QuarantineFactLine[] }) {
  const t = useT();
  if (lines.length === 0) {
    return <p className="t-caption">{t(M.factsEmpty)}</p>;
  }
  return (
    <ul className="facts">
      {lines.map((line) => (
        <li className="facts__row" key={line.key}>
          <span className="facts__key">{dateShort(line.date)}</span>
          <span className="facts__val">{line.text}</span>
        </li>
      ))}
    </ul>
  );
}

/* -------------------------------------------------------------- карта */

export function ContourMapCard({ contourId, crops }: { contourId: string | null; crops?: Crop[] }) {
  const t = useT();
  return (
    <Card size="lg" title={t(M.mapTitle)}>
      <div data-guide="map">
      <MapWidget
        colorBy="geometryStatus"
        crops={crops}
        mode="frame"
        height={240}
        focusIds={contourId ? [contourId] : undefined}
        selectedId={contourId}
        caption={t(M.mapCaption)}
      />
      </div>
    </Card>
  );
}
